import { ReactNode, createContext, useContext, useMemo, useState, useCallback } from 'react';
import { IPlanViewModel, IUserPlanViewModel } from 'viewModels';
import { IPlanInputModel, IPlanUpdateInputModel } from 'inputModels';
import { PlanController } from '../controller/PlanController';
import { useAuth } from './auth';

type PlansContextProps = {
  children: ReactNode;
};

interface IPlansContextValues {
  plans: IPlanViewModel[];
  activePlans: IPlanViewModel[];
  userPlan: IUserPlanViewModel | null;
  loading: boolean;
  error: string | null;
  createPlan: (data: IPlanInputModel) => Promise<IPlanViewModel>;
  updatePlan: (id: string, data: IPlanUpdateInputModel) => Promise<IPlanViewModel>;
  deletePlan: (id: string) => Promise<void>;
  loadPlans: () => Promise<void>;
  loadActivePlans: () => Promise<void>;
  disablePlan: (id: string) => Promise<void>;
  enablePlan: (id: string) => Promise<void>;
  removeUserFromPlan: (userId: string, planId: string) => Promise<void>;
  clearError: () => void;
}

const PlansContext = createContext({} as IPlansContextValues);

const PlansContextProvider = ({ children }: PlansContextProps) => {
  const { setUser, user, tokenIsValid } = useAuth();
  const [plans, setPlans] = useState<IPlanViewModel[]>([]);
  const [activePlans, setActivePlans] = useState<IPlanViewModel[]>([]);
  const [userPlan, setUserPlan] = useState<IUserPlanViewModel | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createPlan = useCallback(async (data: IPlanInputModel): Promise<IPlanViewModel> => {
    setLoading(true);
    setError(null);

    try {
      const id = await PlanController.create(data);
      const newPlan: IPlanViewModel = {
        id,
        ...data,
        users: [],
        createdAt: new Date(),
        updatedAt: new Date(),
        disabled: false
      };
      setPlans(prev => [...prev, newPlan]);
      // Se o plano não está desabilitado, adiciona aos planos ativos
      if (!newPlan.disabled) {
        setActivePlans(prev => [...prev, newPlan]);
      }
      return newPlan;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao criar plano';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const updatePlan = useCallback(async (id: string, data: IPlanUpdateInputModel): Promise<IPlanViewModel> => {
    setLoading(true);
    setError(null);

    try {
      const updatedPlan = await PlanController.update({ ...data, id });
      setPlans(prev => prev.map(plan => plan.id === id ? updatedPlan : plan));

      // Atualiza os planos ativos baseado no status do plano
      if (updatedPlan.disabled) {
        setActivePlans(prev => prev.filter(plan => plan.id !== id));
      } else {
        setActivePlans(prev => {
          const existingIndex = prev.findIndex(plan => plan.id === id);
          if (existingIndex >= 0) {
            // Atualiza o plano existente
            const newActivePlans = [...prev];
            newActivePlans[existingIndex] = updatedPlan;
            return newActivePlans;
          } else {
            // Adiciona o plano se não existir
            return [...prev, updatedPlan];
          }
        });
      }

      return updatedPlan;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao atualizar plano';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const deletePlan = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      await PlanController.delete(id);
      setPlans(prev => prev.filter(plan => plan.id !== id));
      setActivePlans(prev => prev.filter(plan => plan.id !== id));
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao excluir plano';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPlans = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const plansData = await PlanController.getAll();
      setPlans(plansData);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao carregar planos';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadActivePlans = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const plansData = await PlanController.getActive();
      setActivePlans(plansData);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao carregar planos ativos';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // const loadUserPlan = useCallback(async (): Promise<void> => {
  //   setLoading(true);
  //   setError(null);

  //   try {
  //     const userPlanData = await PlanController.getUserPlan();
  //     setUserPlan(userPlanData);
  //   } catch (err: any) {
  //     const errorMessage = err.response?.data?.message || 'Erro ao carregar plano do usuário';
  //     setError(errorMessage);
  //     throw new Error(errorMessage);
  //   } finally {
  //     setLoading(false);
  //   }
  // }, []);

  const disablePlan = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      await PlanController.disable(id);
      setPlans(prev => prev.map(plan =>
        plan.id === id ? { ...plan, disabled: true } : plan
      ));
      setActivePlans(prev => prev.filter(plan => plan.id !== id));
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao desabilitar plano';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const enablePlan = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      await PlanController.enable(id);
      setPlans(prev => prev.map(plan =>
        plan.id === id ? { ...plan, disabled: false } : plan
      ));
      // Recarrega os planos ativos para incluir o plano habilitado
      const activePlansData = await PlanController.getActive();
      setActivePlans(activePlansData);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao habilitar plano';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const removeUserFromPlan = useCallback(async (userId: string, planId: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      await PlanController.removeUserFromPlan(userId, planId);

      // Atualiza a lista local removendo o usuário do plano
      setPlans(prev => prev.map(plan => {
        if (plan.id === planId) {
          return {
            ...plan,
            users: plan.users?.filter(user => user.id !== userId) || []
          };
        }
        return plan;
      }));

      // Atualiza também os planos ativos se necessário
      setActivePlans(prev => prev.map(plan => {
        if (plan.id === planId) {
          return {
            ...plan,
            users: plan.users?.filter(user => user.id !== userId) || []
          };
        }
        return plan;
      }));
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao remover usuário do plano';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // const { isLoading: isLoadingUserPlan } = useQuery('user-plan', () => {
  //   return PlanController.getUserPlan();
  // }, {
  //   onSuccess: (data) => {
  //     setUserPlan(data);
  //     setUser((prev) => ({
  //       ...prev,
  //       userPlan: data,
  //     }));
  //   },
  //   enabled: !!user?.id && tokenIsValid,
  //   refetchOnWindowFocus: false,
  //   refetchOnMount: true,
  //   refetchOnReconnect: true,
  //   refetchInterval: 30000, // 30 segundos
  // });

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const contextValues = useMemo(
    () => ({
      plans,
      activePlans,
      userPlan,
      loading,
      error,
      createPlan,
      updatePlan,
      deletePlan,
      loadPlans,
      loadActivePlans,
      disablePlan,
      enablePlan,
      removeUserFromPlan,
      clearError,
    }),
    [
      plans,
      activePlans,
      userPlan,
      loading,
      error,
      createPlan,
      updatePlan,
      deletePlan,
      loadPlans,
      loadActivePlans,
      disablePlan,
      enablePlan,
      removeUserFromPlan,
      clearError,
    ]
  );

  return <PlansContext.Provider value={contextValues}>{children}</PlansContext.Provider>;
};

export const usePlans = () => useContext(PlansContext);
export default PlansContextProvider;
